import React, { useState, useEffect } from "react";
import { Btn, Inp, Sel, Txa } from "../ui";

import { BRAND } from "../brand";

// ─── CURRENCY OPTIONS — same as receipts ─────────────────────────────────────
const CURRENCIES = [
  { value: "USD", label: "USD – US Dollar" },
  { value: "NGN", label: "NGN – Nigerian Naira" },
  { value: "GBP", label: "GBP – British Pound" },
  { value: "EUR", label: "EUR – Euro" },
  { value: "AED", label: "AED – UAE Dirham" },
  { value: "GHS", label: "GHS – Ghanaian Cedi" },
  { value: "KES", label: "KES – Kenyan Shilling" },
  { value: "ZAR", label: "ZAR – South African Rand" },
  { value: "CAD", label: "CAD – Canadian Dollar" },
  { value: "AUD", label: "AUD – Australian Dollar" },
];

const REFUND_TYPES = [
  { value: "refund", label: "💰 Cash Refund" },
  { value: "kai_credit", label: `💳 ${BRAND.creditName}` },
];

const REFUND_STATUSES = [
  { value: "not_started", label: "Not Started" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

const REFUND_METHODS = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "card", label: "Card" },
  { value: "cash", label: "Cash" },
  { value: "kai_credit", label: BRAND.creditName },
  { value: "other", label: "Other" },
];

function DateInp({ label, value, onChange, min, required, className = "" }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && (
        <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
          {label}
          {required && " * "}
        </label>
      )}
      <input
        type="date"
        value={value || ""}
        onChange={onChange}
        min={min}
        required={required}
        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
      />
    </div>
  );
}

export default function RefundForm({
  initial = {},
  clients = [],
  receipts = [],
  onSubmit,
  onClose,
  saving,
}) {
  const [f, sf] = useState({
    refundReference: initial.refundReference || "",
    receipt: initial.receipt?._id || initial.receipt || "",
    client: initial.client?._id || initial.client || "",
    clientName: initial.clientName || "",
    email: initial.email || "",
    phone: initial.phone || "",
    destination: initial.destination || "",
    currency: initial.currency || BRAND.currency, // default from brand.js
    country: initial.country || "Nigeria",
    refundType: initial.refundType || "refund",
    originalAmount: initial.originalAmount != null ? String(initial.originalAmount) : "",
    refundAmount: initial.refundAmount != null ? String(initial.refundAmount) : "",
    amountApplied: initial.amountApplied != null ? String(initial.amountApplied) : "",
    isKaiCredit: initial.isKaiCredit || initial.refundType === "kai_credit",
    kaiCreditApplied: initial.kaiCreditApplied || false,
    kaiCreditDestination: initial.kaiCreditDestination || "",
    kaiCreditAppliedDate: initial.kaiCreditAppliedDate || "",
    refundStatus: initial.refundStatus || "not_started",
    isClosed: initial.isClosed || false,
    closedDate: initial.closedDate || "",
    refundMethod: initial.refundMethod || "bank_transfer",
    transactionReference: initial.transactionReference || "",
    refundRequestDate: initial.refundRequestDate || new Date().toISOString().split("T")[0],
    refundProcessedDate: initial.refundProcessedDate || "",
    expectedRefundDate: initial.expectedRefundDate || "",
    reasonForRefund: initial.reasonForRefund || "",
    internalNotes: initial.internalNotes || "",
    clientNotes: initial.clientNotes || "",
    bankAccountNumber: initial.bankAccountNumber || "",
    bankAccountName: initial.bankAccountName || "",
    bankName: initial.bankName || "",
  });

  const set = (k) => (e) => sf((p) => ({ ...p, [k]: e.target.value }));
  const setV = (k, v) => sf((p) => ({ ...p, [k]: v }));

  // Auto-toggle isKaiCredit when refundType changes
  useEffect(() => {
    setV("isKaiCredit", f.refundType === "kai_credit");
  }, [f.refundType]);

  // Auto-set kaiCreditAppliedDate when kaiCreditApplied is toggled
  useEffect(() => {
    if (f.kaiCreditApplied && !f.kaiCreditAppliedDate) {
      setV("kaiCreditAppliedDate", new Date().toISOString().split("T")[0]);
    }
  }, [f.kaiCreditApplied]);

  const handleClientSelect = (e) => {
    const id = e.target.value;
    const c = (clients || []).find((x) => x._id === id);
    
    // Get latest receipt/payment info for this client
    const clientReceipts = (receipts || []).filter(r => r.client?._id === id || r.client === id);
    const latestReceipt = clientReceipts[0];
    
    sf((p) => ({
      ...p,
      client: id,
      clientName: c?.fullName || p.clientName,
      email: c?.email || p.email,
      phone: c?.phone || p.phone,
      destination: latestReceipt?.destination || c?.trips?.[c.trips.length - 1]?.destinations?.join(", ") || p.destination,
      currency: latestReceipt?.currency || p.currency, // ✅ Pre-fill currency from receipt
      originalAmount: latestReceipt?.amountPaid || latestReceipt?.total || p.originalAmount,
      refundAmount: latestReceipt?.amountPaid || latestReceipt?.total || p.refundAmount,
    }));
  };

  const handleReceiptSelect = (e) => {
    const id = e.target.value;
    const rct = (receipts || []).find((x) => x._id === id);
    if (!rct) {
      sf((p) => ({ ...p, receipt: id }));
      return;
    }
    sf((p) => ({
      ...p,
      receipt: id,
      client: rct.client?._id || rct.client || p.client,
      clientName: rct.clientName || p.clientName,
      email: rct.email || p.email,
      phone: rct.phone || p.phone,
      destination: rct.destination || p.destination,
      currency: rct.currency || p.currency, // ✅ Pre-fill currency from receipt
      originalAmount: rct.amountPaid || rct.total || p.originalAmount,
      refundAmount: rct.amountPaid || rct.total || p.refundAmount,
    }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!f.clientName.trim() || !f.email.trim()) {
      alert("Client name and email are required");
      return;
    }
    onSubmit({
      ...f,
      refundReference: f.refundReference.trim() || undefined,
      receipt: f.receipt || undefined,
      client: f.client || undefined,
      originalAmount: f.originalAmount !== "" ? parseFloat(f.originalAmount) : 0,
      refundAmount: f.refundAmount !== "" ? parseFloat(f.refundAmount) : 0,
      amountApplied: f.amountApplied !== "" ? parseFloat(f.amountApplied) : undefined,
      kaiCreditAppliedDate: f.kaiCreditAppliedDate || undefined,
      closedDate: f.closedDate || undefined,
      refundRequestDate: f.refundRequestDate || undefined,
      refundProcessedDate: f.refundProcessedDate || undefined,
      expectedRefundDate: f.expectedRefundDate || undefined,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {/* ── Client & Receipt Selection ───────────────────────────────────── */}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {receipts?.length > 0 && (
            <div className="col-span-2">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">
                Link to Receipt (optional)
              </label>
              <select
                value={f.receipt}
                onChange={handleReceiptSelect}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
              >
                <option value="">Select receipt to auto-fill details…</option>
                {(receipts || []).map((rct) => (
                  <option key={rct._id} value={rct._id}>
                    {rct.receiptReference} – {rct.clientName} ({rct.currency})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="col-span-2">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">
              Client
            </label>
            <select
              value={f.client}
              onChange={handleClientSelect}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
            >
              <option value="">Select client (optional — or type below)</option>
              {(clients || []).map((c) => (
                <option key={c._id} value={c._id}>
                  {c.fullName}
                </option>
              ))}
            </select>
          </div>

          <Inp
            label="Client Name *"
            placeholder="Jane Doe"
            value={f.clientName}
            onChange={set("clientName")}
            required
          />
          <Inp
            label="Email *"
            type="email"
            placeholder="jane@example.com"
            value={f.email}
            onChange={set("email")}
            required
          />
          <Inp
            label="Phone"
            placeholder="+234..."
            value={f.phone}
            onChange={set("phone")}
          />
          <Inp
            label="Destination"
            placeholder="Dubai, Abu Dhabi"
            value={f.destination}
            onChange={set("destination")}
          />
        </div>
      </div>

      {/* ── Refund Type & Amount ────────────────────────────────────────── */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4">
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
          Refund Details · {f.currency}
        </p>

        <div className="grid grid-cols-2 gap-4">
          <Sel
            label="Refund Type *"
            value={f.refundType}
            onChange={set("refundType")}
            options={REFUND_TYPES}
          />

          <Sel
            label="Refund Status *"
            value={f.refundStatus}
            onChange={set("refundStatus")}
            options={REFUND_STATUSES}
          />

          {/* ✅ Currency Selection */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
              Currency *
            </label>
            <select
              value={f.currency}
              onChange={set("currency")}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
            >
              {CURRENCIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
              Original Amount
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={f.originalAmount}
              onChange={set("originalAmount")}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] text-right font-semibold"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
              Refund Amount *
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={f.refundAmount}
              onChange={set("refundAmount")}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] text-right font-semibold"
            />
          </div>

          {f.refundType === "kai_credit" && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                  Amount Applied
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={f.amountApplied}
                  onChange={set("amountApplied")}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] text-right"
                />
              </div>

              <Inp
                label={`${BRAND.creditName} Destination`}
                placeholder="Where credit was applied"
                value={f.kaiCreditDestination}
                onChange={set("kaiCreditDestination")}
              />
            </>
          )}
        </div>

        {/* ── Kai Credit Toggle ─────────────────────────────────────────── */}
        {f.refundType === "kai_credit" && (
          <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-xl">
            <input
              type="checkbox"
              id="kaiCreditApplied"
              checked={f.kaiCreditApplied}
              onChange={(e) => setV("kaiCreditApplied", e.target.checked)}
              className="w-4 h-4 text-[rgb(var(--brand-rgb))] rounded focus:ring-[rgb(var(--brand-rgb))]"
            />
            <label htmlFor="kaiCreditApplied" className="text-sm font-semibold text-purple-700">{`✓ ${BRAND.creditName} Applied`}</label>
            {f.kaiCreditApplied && f.kaiCreditAppliedDate && (
              <span className="text-xs text-purple-600 ml-auto">
                Applied on {new Date(f.kaiCreditAppliedDate).toLocaleDateString()}
              </span>
            )}
          </div>
        )}

        {/* ── Closed Toggle ─────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 p-3 bg-slate-100 border border-slate-200 rounded-xl">
          <input
            type="checkbox"
            id="isClosed"
            checked={f.isClosed}
            onChange={(e) => setV("isClosed", e.target.checked)}
            className="w-4 h-4 text-slate-600 rounded focus:ring-slate-500"
          />
          <label htmlFor="isClosed" className="text-sm font-semibold text-slate-700">
            🔒 Closed
          </label>
          {f.isClosed && (
            <DateInp
              value={f.closedDate}
              onChange={set("closedDate")}
              className="ml-auto"
            />
          )}
        </div>
      </div>

      {/* ── Dates & Payment Info ───────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4">
        <DateInp
          label="Refund Request Date"
          value={f.refundRequestDate}
          onChange={set("refundRequestDate")}
        />
        <DateInp
          label="Expected Refund Date"
          value={f.expectedRefundDate}
          onChange={set("expectedRefundDate")}
          min={f.refundRequestDate}
        />
        <DateInp
          label="Refund Processed Date"
          value={f.refundProcessedDate}
          onChange={set("refundProcessedDate")}
          min={f.refundRequestDate}
        />

        <Sel
          label="Refund Method"
          value={f.refundMethod}
          onChange={set("refundMethod")}
          options={REFUND_METHODS}
        />

        <Inp
          label="Transaction Reference"
          placeholder="TXN-20250615-001"
          value={f.transactionReference}
          onChange={set("transactionReference")}
        />
      </div>

      {/* ── Bank Info ──────────────────────────────────────────────────── */}
      {f.refundType === "refund" && (
        <div className="border-t border-slate-200 pt-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
            Bank Account (for refund transfer)
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Inp
              label="Account Number"
              placeholder="0123456789"
              value={f.bankAccountNumber}
              onChange={set("bankAccountNumber")}
            />
            <Inp
              label="Account Name"
              placeholder="Client Name"
              value={f.bankAccountName}
              onChange={set("bankAccountName")}
            />
            <Inp
              label="Bank Name"
              placeholder="First Bank Nigeria"
              value={f.bankName}
              onChange={set("bankName")}
              className="col-span-2"
            />
          </div>
        </div>
      )}

      {/* ── Notes ──────────────────────────────────────────────────────── */}
      <div className="space-y-3">
        <Txa
          label="Reason for Refund"
          placeholder="Why is this refund being requested?"
          value={f.reasonForRefund}
          onChange={set("reasonForRefund")}
          rows={3}
        />
        <Txa
          label="Internal Notes"
          placeholder="For team use only"
          value={f.internalNotes}
          onChange={set("internalNotes")}
          rows={3}
        />
        <Txa
          label="Client Notes"
          placeholder="Message to client"
          value={f.clientNotes}
          onChange={set("clientNotes")}
          rows={3}
        />
      </div>

      <div className="flex gap-3 pt-2 border-t border-slate-100">
        <Btn
          type="submit"
          variant="primary"
          className="flex-1"
          loading={saving}
        >
          {initial._id ? "Update Refund" : "Create Refund"}
        </Btn>
        <Btn type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Btn>
      </div>
    </form>
  );
}